import type { TutorialStep } from "../constants/activationContent";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowRight } from "lucide-react";

interface TutorialCardProps {
  content: TutorialStep;
  /** 1-indexed */
  currentStep?: number;
  totalSteps?: number;
  onNext?: () => void;
  onSkip?: () => void;
  primaryButtonLabel?: string;
  skipButtonLabel?: string;
  showSkipButton?: boolean;
  showStepIndicator?: boolean;
  className?: string;
}

export function TutorialCard({
  content,
  currentStep,
  totalSteps,
  onNext,
  onSkip,
  primaryButtonLabel = "Next",
  skipButtonLabel = "Skip tutorial",
  showSkipButton = true,
  showStepIndicator = true,
  className,
}: TutorialCardProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [loadedContentId, setLoadedContentId] = React.useState<string | null>(
    null,
  );
  const isMediaLoaded = loadedContentId === content.id;

  return (
    <div
      className={twMerge(
        "flex flex-col gap-4 rounded-xl bg-black p-4 text-white",
        className,
      )}
    >
      {/* Media Area */}
      {(content.videoUrl ?? content.imageUrl) && (
        <div className="relative h-60 w-full overflow-hidden rounded-md">
          {!isMediaLoaded && <Skeleton className="absolute inset-0" />}
          {content.videoUrl ? (
            <video
              ref={videoRef}
              src={content.videoUrl}
              className={twMerge(
                "size-full object-cover transition-opacity duration-300",
                isMediaLoaded ? "opacity-100" : "opacity-0",
              )}
              autoPlay
              muted
              loop
              playsInline
              onCanPlay={() => setLoadedContentId(content.id)}
            />
          ) : (
            <img
              src={content.imageUrl}
              alt={content.title}
              className={twMerge(
                "size-full object-cover transition-opacity duration-300",
                isMediaLoaded ? "opacity-100" : "opacity-0",
              )}
              onLoad={() => setLoadedContentId(content.id)}
            />
          )}
          {/* Bottom gradient fade */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col gap-1">
        <h3 className="font-editorial header-h3">{content.title}</h3>
        <p className="text-base text-white/80">{content.body}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span
          className={twMerge(
            "text-base",
            !(showStepIndicator && currentStep && totalSteps) && "hidden",
          )}
        >
          {currentStep} of {totalSteps}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-6">
          {showSkipButton && onSkip && (
            <Button
              variant="tertiary"
              size="base"
              onClick={onSkip}
              UNSAFE_className="cursor-pointer text-white hover:bg-white/10"
            >
              {skipButtonLabel}
            </Button>
          )}

          {onNext && (
            <Button
              variant="secondary"
              size="base"
              onClick={onNext}
              endEnhancer={<ArrowRight />}
              UNSAFE_className="cursor-pointer"
            >
              {primaryButtonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
